import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { trpc } from '@/lib/trpc'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { useState, useEffect, useCallback } from 'react'
import { Loader2 } from 'lucide-react'
import { useQueryState, parseAsInteger } from 'nuqs'
import { ContextScopeV0Wizard } from '@/components/screenata/ContextScopeV0Wizard'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/screenata/',
)({
  component: ScreenataPage,
})

function ScreenataPage() {
  const { workspaceId } = useParams({ from: '/(protected)/workspaces/$workspaceId/screenata/' })
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [programId, setProgramId] = useState<string | null>(null)
  const [urlStep, setUrlStep] = useQueryState('step', parseAsInteger)

  // Initialize or get compliance program
  const initMutation = useMutation(trpc.complianceProgram.initProgram.mutationOptions())
  const programQuery = useQuery(trpc.complianceProgram.getProgram.queryOptions({ framework: 'SOC2' }))

  useEffect(() => {
    if (programQuery.data?.id) {
      setProgramId(programQuery.data.id)
    }
  }, [programQuery.data])

  const handleInitProgram = useCallback(async () => {
    if (!programId) {
      const result = await initMutation.mutateAsync({ framework: 'SOC2' })
      setProgramId(result.id)
      queryClient.invalidateQueries({ queryKey: trpc.complianceProgram.getProgram.queryKey() })
    }
  }, [programId, initMutation, queryClient])

  const handleStepChange = useCallback((s: number) => {
    setUrlStep(s)
    // Create program when leaving the welcome screen
    if (s >= 1 && !programId) {
      handleInitProgram()
    }
  }, [setUrlStep, programId, handleInitProgram])

  // Redirect to the first incomplete onboarding step when program exists and context is done
  // Skip redirect when urlStep is set — user explicitly navigated back to review/edit wizard answers
  useEffect(() => {
    if (!programQuery.data || !programId) return
    if (urlStep != null) return
    const p = programQuery.data
    if (!p.contextScopeV0Complete) return // wizard handles this

    // Find the first incomplete step and redirect
    if (!(p.githubConnected || p.scanningSkipped)) {
      navigate({ to: '/workspaces/$workspaceId/screenata/connect-repo', params: { workspaceId }, replace: true })
    } else if (!(p.integrationsComplete || p.cloudConnected || p.scanningSkipped)) {
      navigate({ to: '/workspaces/$workspaceId/screenata/integrations', params: { workspaceId }, replace: true })
    } else if (!p.scopeV1ConfirmedAt) {
      navigate({ to: '/workspaces/$workspaceId/screenata/scope-confirm', params: { workspaceId }, replace: true })
    } else if (p.claimTriageEnabled) {
      // Triage-enabled programs: policies hub is the central step after scope confirm.
      // The policies page has a "Continue to Readiness" CTA when gate is open.
      if (p.testPlanPhase === 'complete') {
        navigate({ to: '/workspaces/$workspaceId/screenata/readiness', params: { workspaceId }, replace: true })
      } else {
        navigate({ to: '/workspaces/$workspaceId/screenata/generate-policies', params: { workspaceId }, replace: true })
      }
    } else if (p.policiesGenerated < 1) {
      navigate({ to: '/workspaces/$workspaceId/screenata/generate-policies', params: { workspaceId }, replace: true })
    } else {
      navigate({ to: '/workspaces/$workspaceId/screenata/readiness', params: { workspaceId }, replace: true })
    }
  }, [programQuery.data, programId, navigate, workspaceId, urlStep])

  // Show wizard when scope v0 is incomplete, OR when user explicitly set a step param to review
  if (!programQuery.isLoading && (!programQuery.data?.contextScopeV0Complete || urlStep != null)) {
    return (
      <ContextScopeV0Wizard
        programId={programId ?? undefined}
        step={urlStep != null ? urlStep : (programId ? undefined : 0)}
        onStepChange={handleStepChange}
        onComplete={() => {
          queryClient.invalidateQueries({ queryKey: trpc.complianceProgram.getProgram.queryKey() })
          navigate({
            to: '/workspaces/$workspaceId/screenata/connect-repo',
            params: { workspaceId },
          })
        }}
      />
    )
  }

  // Loading / redirecting to next step
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="size-5 animate-spin text-muted-foreground" />
    </div>
  )
}
